import React from 'react';

/**
 * Small numbered-pagination control used across admin tables (Activity Log,
 * Conversations). Renders Prev / 1 2 3 … N / Next. Page index is 0-based to
 * match existing call sites. Hides itself when there's only one page.
 *
 *   <Pagination page={p} totalPages={t} onChange={setP} dataTestId="..." />
 */
export default function Pagination({ page, totalPages, onChange, dataTestId }) {
  if (!totalPages || totalPages <= 1) return null;
  const cur = Math.max(0, Math.min(page, totalPages - 1));

  // Build the visible page list — keep around 7 buttons max.
  const pages = [];
  const push = (v) => pages.push(v);
  if (totalPages <= 7) {
    for (let i = 0; i < totalPages; i += 1) push(i);
  } else {
    push(0);
    if (cur > 2) push('…');
    const start = Math.max(1, cur - 1);
    const end = Math.min(totalPages - 2, cur + 1);
    for (let i = start; i <= end; i += 1) push(i);
    if (cur < totalPages - 3) push('…');
    push(totalPages - 1);
  }

  const goto = (next) => {
    if (typeof next === 'number' && next !== cur) onChange(next);
  };

  return (
    <nav
      className="flex flex-wrap items-center justify-center gap-1 mt-4"
      data-testid={dataTestId || 'admin-pagination'}
      aria-label="Pagination"
    >
      <button
        type="button"
        onClick={() => goto(Math.max(0, cur - 1))}
        disabled={cur === 0}
        className="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-200 bg-white disabled:opacity-30 hover:bg-gray-50"
        data-testid="pagination-prev"
      >
        Prev
      </button>
      {pages.map((p, i) =>
        p === '…' ? (
          <span key={`gap-${i}`} className="px-1 text-sm text-gray-400 select-none">…</span>
        ) : (
          <button
            key={p}
            type="button"
            onClick={() => goto(p)}
            aria-current={p === cur ? 'page' : undefined}
            className={`min-w-[36px] px-2.5 py-1.5 text-sm font-medium rounded-md border ${
              p === cur
                ? 'bg-[#2E7D32] text-white border-[#1B5E20] shadow-sm'
                : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
            }`}
            data-testid={`pagination-page-${p + 1}`}
          >
            {p + 1}
          </button>
        )
      )}
      <button
        type="button"
        onClick={() => goto(Math.min(totalPages - 1, cur + 1))}
        disabled={cur >= totalPages - 1}
        className="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-200 bg-white disabled:opacity-30 hover:bg-gray-50"
        data-testid="pagination-next"
      >
        Next
      </button>
    </nav>
  );
}
